import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import useAuth from '../hooks/AuthHook.jsx';
import { LocumPicker, StorePicker } from './AdminPickers';
import { HelpPanel, LocumCard, OptionsPanel, PreviewStep, ResultStep, ShiftRowsTable, StoreCard, ToggleField } from './AdminAssignSteps';
import { ADMIN_MODE } from './bookingModes';
import {
  DEFAULT_SETTINGS, MAX_SHIFTS_PER_BOOKING, buildPayload, buildRows, compareRows, formatServerErrors,
  fromDateString, getDatesInRange, getRowPhase, getRowProblems, rememberStore, toDateString,
} from './adminShiftUtils';

import './AdminAssignShiftsPage.scss';

const STEPS = { EDIT: 'EDIT', PREVIEW: 'PREVIEW', DONE: 'DONE' };
const DEFAULT_OPTIONS = { notifyLocum: false, suppressCompletionReminders: false, allowOldShifts: false, allowDuplicateShifts: false };

/** The calendar is memoised so typing in the time and rate boxes does not redraw two months of days. */
const SelectableCalendar = React.memo(({ selectedDates, rangeFrom, onToggle }) => (
  <DatePicker
    key={rangeFrom || 'no-range'}
    openToDate={rangeFrom ? fromDateString(rangeFrom) : undefined}
    selected={null}
    onChange={onToggle}
    highlightDates={selectedDates}
    formatWeekDay={nameOfDay => nameOfDay.substr(0, 3)}
    monthsShown={2}
    inline
  />
));

const AdminAssignShiftsPage = ({ mode = ADMIN_MODE }) => {
  const { profile } = useAuth();
  const stores = profile.stores;
  const [locum, setLocum] = useState(null);
  const [store, setStore] = useState(null);
  const [manualAdds, setManualAdds] = useState([]);
  const [manualRemoves, setManualRemoves] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [step, setStep] = useState(STEPS.EDIT);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const errorRef = useRef(null);

  // Errors appear next to the button that caused them, which can be far down the page, so bring them into view.
  useEffect(() => {
    if (errors.length > 0 && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  // Review, back, confirm and book-again replace a long page. Land at the top unless an error list needs to be seen.
  const skipStepScroll = useRef(true);
  useEffect(() => {
    if (skipStepScroll.current) {
      skipStepScroll.current = false;
      return;
    }
    if (errors.length > 0) {
      return;
    }
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    // errors is read so a failed confirm stays on the error list. Adding a shift also clears errors and must not scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const sortedRows = useMemo(() => [...rows].sort(compareRows), [rows]);
  const storeCounts = useMemo(() => {
    const counts = new Map();
    rows.forEach(row => {
      const current = counts.get(row.storeId) || { storeId: row.storeId, storeName: row.storeName, count: 0 };
      counts.set(row.storeId, { ...current, count: current.count + 1 });
    });
    return [...counts.values()];
  }, [rows]);
  // Phases and row checks only change when rows do, so they are not recomputed while typing in the builder.
  const { hasEndedRows, hasUpcomingRows, hasRowProblems } = useMemo(() => {
    const phases = sortedRows.map(row => getRowPhase(row));
    return {
      hasEndedRows: phases.includes('ENDED'),
      hasUpcomingRows: phases.some(phase => phase === 'UPCOMING' || phase === 'IN_PROGRESS' || phase === null),
      hasRowProblems: sortedRows.some(row => getRowProblems(row).length > 0),
    };
  }, [sortedRows]);
  const settingsProblems = getRowProblems({ ...settings, date: '' });
  // Selected dates = the From/To range (minus weekends when skipped), plus dates clicked on, minus dates clicked off.
  const rangeIsValid = !!rangeFrom && !!rangeTo && rangeTo >= rangeFrom;
  const allRangeDates = useMemo(
    () => (rangeIsValid ? getDatesInRange(rangeFrom, rangeTo, skipWeekends) : []),
    [rangeIsValid, rangeFrom, rangeTo, skipWeekends],
  );
  // A range that is too long is ignored, so a half-typed year in the date box can't select thousands of days.
  const rangeIsTooLong = allRangeDates.length > MAX_SHIFTS_PER_BOOKING;
  const rangeDates = useMemo(() => (rangeIsTooLong ? [] : allRangeDates), [rangeIsTooLong, allRangeDates]);
  const dates = useMemo(
    () => [...new Set([...rangeDates.filter(date => !manualRemoves.includes(date)), ...manualAdds])].sort(),
    [rangeDates, manualAdds, manualRemoves],
  );
  const selectedDates = useMemo(() => dates.map(fromDateString), [dates]);
  let rangeProblem = '';
  if (rangeFrom && rangeTo && rangeTo < rangeFrom) {
    rangeProblem = 'The end date is before the start date.';
  } else if (rangeIsTooLong) {
    rangeProblem = `That range is longer than ${MAX_SHIFTS_PER_BOOKING} days, so no dates were selected. Check the years.`;
  }
  const canPreview = !!locum && sortedRows.length > 0 && !hasRowProblems && !submitting;
  const needsOldShiftOverride = errors.some(message => message.includes('ended more than 29 days ago')) || options.allowOldShifts;
  const needsDuplicateOverride = errors.some(message => message.includes('a shift already exists')) || options.allowDuplicateShifts;

  const updateSettings = (changes) => setSettings(current => ({ ...current, ...changes }));
  const updateOptions = (changes) => setOptions(current => ({ ...current, ...changes }));
  const updateRow = useCallback((id, changes) => setRows(current => current.map(row => (row.id === id ? { ...row, ...changes } : row))), []);
  const removeRow = useCallback((id) => setRows(current => current.filter(row => row.id !== id)), []);

  const selectStore = (selected) => {
    // Switching to a different store starts the calendar fresh. Shifts already added to the table are kept.
    if (selected._id !== store?._id) {
      clearDates();
    }
    setStore(selected);
    rememberStore(selected._id);
  };

  // The latest selection is read through a ref so toggleDate keeps one identity and the memoised calendar is not redrawn.
  const latestSelection = useRef({});
  latestSelection.current = { dates, manualAdds, manualRemoves };
  const toggleDate = useCallback((date) => {
    const dateString = toDateString(date);
    const { dates: currentDates, manualAdds: currentAdds, manualRemoves: currentRemoves } = latestSelection.current;
    if (currentDates.includes(dateString)) {
      if (currentAdds.includes(dateString)) {
        setManualAdds(current => current.filter(item => item !== dateString));
      } else {
        setManualRemoves(current => [...current, dateString]);
      }
    } else if (currentRemoves.includes(dateString)) {
      setManualRemoves(current => current.filter(item => item !== dateString));
    } else {
      setManualAdds(current => [...current, dateString]);
    }
  }, []);

  const clearManualChanges = () => {
    setManualAdds([]);
    setManualRemoves([]);
  };

  const changeRange = (changes) => {
    // A new range starts fresh, so dates clicked on or off for the old range are forgotten.
    if (changes.from !== undefined) {
      setRangeFrom(changes.from);
    }
    if (changes.to !== undefined) {
      setRangeTo(changes.to);
    }
    clearManualChanges();
  };

  const clearDates = () => {
    setRangeFrom('');
    setRangeTo('');
    clearManualChanges();
  };

  const addShiftRows = () => {
    const { rows: newRows, skipped } = buildRows({ store, dates, settings, existingRows: rows });
    if (rows.length + newRows.length > MAX_SHIFTS_PER_BOOKING) {
      setErrors([`A maximum of ${MAX_SHIFTS_PER_BOOKING} shifts can be booked at once.`]);
      return;
    }
    setErrors([]);
    setRows(current => [...current, ...newRows]);
    // Dates stay selected so the same dates can be added for the next store. Use "Clear dates" to start fresh.
    setNotice(`${skipped > 0 ? `Added ${newRows.length} shift(s), skipped ${skipped} already in the list.` : `Added ${newRows.length} shift(s) at ${store.name}.`} The dates are still selected: pick another store and click Add to put the same dates there too, or change the dates first. Nothing is booked until you click Review booking and confirm.`);
  };

  const applySettingsToAllRows = () => {
    setRows(current => current.map(row => ({
      ...row,
      start: settings.start,
      end: settings.end,
      unpaidBreakMinutes: String(settings.unpaidBreakMinutes),
      rate: String(settings.rate),
      negotiable: settings.negotiable,
      billToMedicare: settings.billToMedicare,
    })));
    setNotice('Applied the times, break and rate above to every row.');
  };

  const runPreview = async () => {
    setSubmitting(true);
    setErrors([]);
    setNotice('');
    try {
      const { plan } = await mode.previewBooking(buildPayload({ locum, rows: sortedRows, options }));
      setPreview(plan);
      setStep(STEPS.PREVIEW);
    } catch (error) {
      setErrors(error.response && error.response.errors ? formatServerErrors(error.response.errors) : [error.message]);
    }
    setSubmitting(false);
  };

  const confirmBooking = async () => {
    setSubmitting(true);
    setErrors([]);
    try {
      const booked = await mode.confirmBooking(buildPayload({ locum, rows: sortedRows, options }));
      setResult(booked);
      setStep(STEPS.DONE);
    } catch (error) {
      // Nothing was written on a validation failure, so go back to editing with the reasons.
      if (error.response && error.response.errors) {
        setErrors(formatServerErrors(error.response.errors));
      } else if (error.status) {
        setErrors([error.message]);
      } else {
        // No response at all (network drop or timeout): the booking may or may not have been saved.
        setErrors([`${error.message}. The booking may have been saved. Check the locum's schedule before trying again: booking the same shifts twice is blocked, and a clash error here usually means it did save.`]);
      }
      setStep(STEPS.EDIT);
    }
    setSubmitting(false);
  };

  const resetForNextBooking = (keepLocum) => {
    if (!keepLocum) {
      setLocum(null);
    }
    setRows([]);
    clearDates();
    setOptions(DEFAULT_OPTIONS);
    setPreview(null);
    setResult(null);
    setErrors([]);
    setNotice('');
    setStep(STEPS.EDIT);
  };

  if (step === STEPS.DONE && result) {
    return (
      <main className={'admin-assign-page'}>
        <h1 className={'page-title'}>{'Assign shifts to a locum'}</h1>
        <div className={'step-enter'} key={step}>
          <ResultStep result={result} onBookMore={() => resetForNextBooking(true)} onStartOver={() => resetForNextBooking(false)} />
        </div>
      </main>
    );
  }

  return (
    <main className={'admin-assign-page'}>
      <h1 className={'page-title'}>{'Assign shifts to a locum'}</h1>
      <p className={'page-subtitle'}>
        {'Creates shifts as if the locum had requested and been accepted. Past shifts can be marked complete and invoiced as usual.'}
      </p>

      <div className={'step-enter'} key={step}>
      { step === STEPS.EDIT && <HelpPanel isBusiness={mode.isBusiness} /> }

      { step === STEPS.PREVIEW && preview
        ? <PreviewStep
            preview={preview}
            options={options}
            errors={errors}
            submitting={submitting}
            onBack={() => { setStep(STEPS.EDIT); setErrors([]); }}
            onConfirm={confirmBooking}
          />
        : <>
            <section className={'step-panel'}>
              <h2 className={'step-title'}>{mode.isBusiness ? '1. Locum (only locums who have worked at your stores)' : '1. Locum'}</h2>
              <LocumPicker locum={locum} onSelect={setLocum} searchLocums={mode.searchLocums} isBusiness={mode.isBusiness} />
              { locum && <LocumCard locum={locum} /> }
            </section>

            <section className={'step-panel'}>
              <h2 className={'step-title'}>{mode.isBusiness ? '2. Add shifts (any of your stores, one booking)' : '2. Add shifts (any number of stores, one booking)'}</h2>
              <p className={'step-help'}>
                {'You do not need to finish one store before starting another, or start over between stores. Pick a store and dates, click Add, then pick another store (and different dates if needed) and click Add again. Everything you add collects in the list below as a draft. Nothing is booked until you click Review booking and confirm, and then everything is booked together in one go.'}
              </p>
              { storeCounts.length > 0 &&
                <div className={'added-summary'}>
                  <strong>{`Added so far: ${rows.length} shift${rows.length === 1 ? '' : 's'} across ${storeCounts.length} store${storeCounts.length === 1 ? '' : 's'}`}</strong>
                  <ul>{storeCounts.map(({ storeId, storeName, count }) => <li key={storeId}>{`${storeName}: ${count}`}</li>)}</ul>
                </div>
              }
              <StorePicker stores={stores} store={store} onSelect={selectStore} searchRemoteStores={mode.searchStores} />
              { store && <StoreCard store={store} /> }
              <div className={'builder'}>
                <div className={'builder-calendar'}>
                  <SelectableCalendar selectedDates={selectedDates} rangeFrom={rangeFrom} onToggle={toggleDate} />
                  <div className={'range-row'}>
                    <label>{'From'}<input type={'date'} value={rangeFrom} onChange={event => changeRange({ from: event.target.value })} /></label>
                    <label>{'To'}<input type={'date'} value={rangeTo} onChange={event => changeRange({ to: event.target.value })} /></label>
                    <label className={'inline-check'}>
                      <input type={'checkbox'} checked={skipWeekends} onChange={event => setSkipWeekends(event.target.checked)} />
                      {'Skip weekends'}
                    </label>
                    <button type={'button'} className={'secondary-button small'} onClick={clearDates} disabled={dates.length === 0 && !rangeFrom && !rangeTo}>{'Clear dates'}</button>
                  </div>
                  <span className={rangeProblem ? 'problem-text' : 'hint'}>
                    {rangeProblem || 'Enter From and To to select every day in between (weekends left out when ticked). Click any day on the calendar to add or remove it.'}
                  </span>
                </div>
                <div className={'builder-settings'}>
                  <div className={'step-callout'} role={'note'}>
                    <p><strong>{'These times and the rate apply to every date selected.'}</strong>{' You can change the start time, end time, break and rate of any individual shift in the next step.'}</p>
                    <p><strong>{'Nothing is booked until you click Review booking and confirm.'}</strong></p>
                  </div>
                  <div className={'field-grid'}>
                    <label>{'Start'}<input type={'time'} value={settings.start} onChange={event => updateSettings({ start: event.target.value })} /></label>
                    <label>{'End'}<input type={'time'} value={settings.end} onChange={event => updateSettings({ end: event.target.value })} /></label>
                    <label>{'Unpaid break (min)'}<input type={'number'} min={0} step={5} value={settings.unpaidBreakMinutes} onChange={event => updateSettings({ unpaidBreakMinutes: event.target.value })} /></label>
                    <label>{'Rate ($/hour)'}<input type={'number'} min={0} step={0.5} value={settings.rate} placeholder={'e.g. 85'} onChange={event => updateSettings({ rate: event.target.value })} /></label>
                  </div>
                  <ToggleField
                    label={'Bill to Medicare'}
                    checked={settings.billToMedicare}
                    onChange={value => updateSettings({ billToMedicare: value, ...(value && settings.rate === '' ? { rate: '0' } : {}) })}
                    onText={'The locum bills Medicare directly. The shift shows "NA" for rate and total, a rate of 0 is allowed (filled in for you), and when the locum completes the shift no Stripe invoice is created. The shift is simply marked completed.'}
                    offText={'Normal paid shift: the rate is required and, when the locum completes the shift, an invoice is created for the store.'}
                  />
                  <ToggleField
                    label={'Rate negotiable'}
                    checked={settings.negotiable}
                    onChange={value => updateSettings({ negotiable: value })}
                    onText={'The shift is labelled "(rate negotiable)" in shift listings. Label only: the rate you enter is still what is booked and invoiced.'}
                    offText={'The shift is labelled "(rate fixed)". The rate you enter is booked and invoiced.'}
                  />
                  <div className={'builder-summary'}>
                    {store ? `${dates.length} date(s) selected for ${store.name}${skipWeekends && rangeIsValid ? ' (weekends skipped)' : ''}` : 'Pick a store to add shifts'}
                  </div>
                  <button
                    type={'button'}
                    className={'primary-button'}
                    onClick={addShiftRows}
                    disabled={!store || dates.length === 0 || settingsProblems.length > 0}
                  >
                    {dates.length > 0 ? `Add ${dates.length} shift${dates.length === 1 ? '' : 's'}` : 'Add shifts'}
                  </button>
                  { store && dates.length > 0 && settingsProblems.length > 0 && <span className={'problem-text'}>{settingsProblems[0]}</span> }
                  { notice && <span className={'hint'}>{notice}</span> }
                </div>
              </div>
            </section>

            { sortedRows.length > 0 &&
              <section className={'step-panel'}>
                <div className={'step-title-row'}>
                  <h2 className={'step-title'}>{`3. Shifts to book (${sortedRows.length}) - not booked yet`}</h2>
                  <div className={'step-title-actions'}>
                    <button type={'button'} className={'secondary-button small'} onClick={applySettingsToAllRows} disabled={settingsProblems.length > 0}>{'Apply times & rate above to all rows'}</button>
                    <button type={'button'} className={'secondary-button small'} onClick={() => setRows([])}>{'Clear all'}</button>
                  </div>
                </div>
                <p className={'hint'}>{'This list is only a draft. Nothing is booked until you click Review booking below and confirm on the next screen.'}</p>
                <ShiftRowsTable rows={sortedRows} onChange={updateRow} onRemove={removeRow} />
              </section>
            }

            { sortedRows.length > 0 &&
              <OptionsPanel
                options={options}
                onChange={updateOptions}
                hasEndedRows={hasEndedRows}
                hasUpcomingRows={hasUpcomingRows}
                needsOldShiftOverride={needsOldShiftOverride}
                needsDuplicateOverride={needsDuplicateOverride}
              />
            }

            { errors.length > 0 &&
              <ul className={'error-list'} role={'alert'} ref={errorRef}>{errors.map(message => <li key={message}>{message}</li>)}</ul>
            }

            <div className={'actions'}>
              <button type={'button'} className={'primary-button large'} onClick={runPreview} disabled={!canPreview}>
                {submitting ? 'Checking...' : 'Review booking'}
              </button>
              { !locum && sortedRows.length > 0 && <span className={'hint'}>{'Choose a locum first.'}</span> }
              { locum && sortedRows.length > 0 && <span className={'hint'}>{'This only checks the details. Nothing is booked until you confirm on the next screen.'}</span> }
            </div>
          </>
      }
      </div>
    </main>
  );
};

AdminAssignShiftsPage.displayName = 'AdminAssignShiftsPage';
export default AdminAssignShiftsPage;
